import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { cls } from "@wss/cls";
import debounce from "lodash/debounce";
import Icon from "~/common/Icons/Standard";

export { default as Item } from "./Item";

const buttonClasses = {
  navy: cls("bg-navy-blue-370", "hover:bg-navy-blue-395"),
  white: cls("bg-white", "border-gray-200", "border-solid"),
  pink: cls("bg-white"),
  gray: cls("bg-white", "border-gray-200", "border-solid"),
};

const iconClasses = {
  navy: cls("text-white", "group-hover:text-gray-200"),
  white: cls("text-green-400"),
  pink: cls("text-green-400"),
  gray: cls("text-green-400"),
};

const bgColorClasses = {
  navy: cls("px-3", "py-5", "bg-navy-blue-300", "xs:p-7"),
  white: cls("bg-white", "border-gray-200", "border-solid", "border"),
  pink: cls("bg-white"),
  gray: cls("bg-gray-50", "border-gray-200", "border-solid", "border"),
};

interface Props {
  children: React.ReactNode;
  colorVariant?: keyof typeof buttonClasses;
  className?: ClassTypes | undefined;
}
type SlideRightProps = {
  slider: React.RefObject<HTMLElement>;
  slidePosition: number;
  onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  setSliderRight: React.Dispatch<React.SetStateAction<boolean>>;
  setSlideScroll: React.Dispatch<React.SetStateAction<number>>;
  slide: React.RefObject<HTMLLIElement>;
  colorVariant?: keyof typeof buttonClasses;
};

const SlideRight = ({
  slider,
  slidePosition,
  onClick,
  setSliderRight,
  setSlideScroll,
  slide,
  colorVariant = "white",
}: SlideRightProps) => {
  const [sliderWidth, setSliderWidth] = useState(0);
  const [windowSize, setWindowSize] = useState(0);

  const colorStyles = buttonClasses[colorVariant];
  const iconStyles = iconClasses[colorVariant];

  useEffect(() => {
    if (slider.current) {
      setSliderWidth(slider.current.scrollWidth - slider.current.clientWidth);
      setSlideScroll(Math.round(slider.current.clientWidth * 0.8));
      if (slider.current.scrollWidth - slider.current.clientWidth > 0) {
        setSliderRight(true);
      }
    }
    const handleResize = debounce(() => {
      setWindowSize(window.innerWidth);
    }, 300);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [
    setSlideScroll,
    setSliderRight,
    slide,
    slidePosition,
    slider,
    sliderWidth,
    windowSize,
  ]);
  return (
    <>
      {sliderWidth > 0 && slidePosition < sliderWidth - 25 && (
        <button
          aria-label="Show next set of slides"
          className={cls(
            "group",
            "rounded-r",
            "border-0",
            "cursor-pointer",
            "hidden",
            "h-full",
            "mx-0",
            "absolute",
            "top-0",
            "right-0",
            "xs:inline-block",
            colorStyles,
            colorVariant === "white" ? cls("border-l") : ""
          )}
          type="button"
          onClick={onClick}
        >
          <div
            style={{
              left: "-16px",
              boxShadow: "inset -5px 0 8px -5px rgba(0, 0, 0, 0.25)",
            }}
            className={cls("h-full", "absolute", "top-0", "w-4")}
          />
          <Icon
            name="right-open"
            className={cls("h-5", "fill-current", "w-5", iconStyles)}
          />
        </button>
      )}
    </>
  );
};
export default function Carousel(props: Props) {
  const {
    children,
    colorVariant = "white",
    className = cls("rounded"),
  } = props;
  const [slidePosition, setSlidePosition] = useState(0);
  const slider = useRef<HTMLUListElement>(null);
  const [sliderRight, setSliderRight] = useState(false);
  const [slideScroll, setSlideScroll] = useState(300);
  const slide = useRef<HTMLLIElement>(null);

  useEffect(() => {
    setSlidePosition(0);
  }, [children]);

  const carouselListContainerClasses =
    colorVariant === "navy" ? cls("h-full") : "";

  const buttonStyles = buttonClasses[colorVariant];
  const iconStyles = iconClasses[colorVariant];
  const bgStyles = bgColorClasses[colorVariant];

  const handleSlide = (direction: string) => {
    if (slider.current) {
      slider.current.scrollLeft -=
        direction === "left" ? slideScroll : -slideScroll;
    }
  };
  const handleScroll = (e: React.UIEvent<HTMLUListElement, UIEvent>) => {
    setSlidePosition(e.currentTarget.scrollLeft);
  };

  /** This will run twice, once on mount and once when the ref is set to the slider */
  useLayoutEffect(() => {
    if (slider.current) {
      if (slider.current.scrollLeft > 0) {
        /**
         * If the browser has scrolled the carousel based on layout shift,
         * we will reset it back to 0.
         */
        slider.current.scrollLeft = 0;
      }
    }
  }, [children, slider]);

  return (
    <div
      className={cls(
        className,
        "box-border",
        "h-full",
        "overflow-hidden",
        "relative",
        "w-full",
        bgStyles
      )}
    >
      {slidePosition > 0 && (
        <button
          aria-label="Show previous set of slides"
          className={cls(
            "group",
            "rounded-l",
            "border-0",
            "cursor-pointer",
            "hidden",
            "h-full",
            "mx-0",
            "absolute",
            "top-0",
            "left-0",
            "xs:inline-block",
            "z-5",
            buttonStyles,
            colorVariant === "white" ? cls("border-r") : ""
          )}
          type="button"
          onClick={() => handleSlide("left")}
        >
          <div
            style={{
              right: "-16px",
              boxShadow: "inset 5px 0 8px -5px rgba(0, 0, 0, 0.25)",
            }}
            className={cls("h-full", "absolute", "top-0", "w-4")}
          />
          <Icon
            name="left-open"
            className={cls("h-5", "fill-current", "w-5", iconStyles)}
          />
        </button>
      )}
      <ul
        onScroll={handleScroll}
        className={cls(
          "scrollbar-width-none",
          "flex",
          "mb-0",
          "ml-0",
          "overflow-x-auto",
          "relative",
          "scroll-snap-x",
          "scroll-smooth",
          carouselListContainerClasses
        )}
        ref={slider}
      >
        {React.Children.map(children, (child) =>
          child && typeof child === "object" && "props" in child
            ? React.createElement(child.type, {
                ...{
                  ...child.props,
                  sliderRight,
                },
              })
            : child
        )}
      </ul>

      <SlideRight
        slider={slider}
        slidePosition={slidePosition}
        onClick={() => handleSlide("right")}
        setSliderRight={setSliderRight}
        slide={slide}
        setSlideScroll={setSlideScroll}
        colorVariant={colorVariant}
      />
    </div>
  );
}
